import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Anime } from "@/types/anime";
import { formatDate } from "@/utils/anime";
import { useFavorites } from "@/hooks/useFavorites";

interface HeroCarouselProps {
  items: Anime[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const { isFavorite, toggleFavorite } = useFavorites();

  const next = useCallback(() => {
    setIndex((prev) => (prev + 1) % items.length);
  }, [items.length]);

  const prev = useCallback(() => {
    setIndex((current) => (current - 1 + items.length) % items.length);
  }, [items.length]);

  useEffect(() => {
    if (paused || items.length <= 1) return;
    const timer = window.setInterval(next, 6500);
    return () => window.clearInterval(timer);
  }, [next, paused, items.length]);

  if (items.length === 0) return null;
  const current = items[index];
  const saved = isFavorite(current.id);

  return (
    <section
      className="relative w-full h-[520px] md:h-[600px] overflow-hidden bg-background-100"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div key={current.id} className="absolute inset-0 animate-soft-fade">
        <img
          src={current.banner}
          alt={`${current.title} 主视觉`}
          title={`${current.title} ${current.category} ${current.year}`}
          className="w-full h-full object-cover object-top animate-hero-shift"
        />
      </div>
      <div className="absolute inset-0 hero-mask" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-background-50 to-transparent" />

      <div className="relative z-10 h-full w-full px-4 md:px-8 lg:px-12 flex flex-col justify-end pb-16 md:pb-20">
        <div key={`content-${current.id}`} className="max-w-xl animate-fade-up">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="inline-flex items-center h-6 px-2 rounded bg-primary-600/90 text-foreground-950 text-[11px] tracking-wider">
              焦点推荐
            </span>
            <span className="inline-flex items-center h-6 px-2 rounded border border-accent-600/50 text-accent-400 text-[11px]">
              {current.category}
            </span>
            <span className="inline-flex items-center gap-1 h-6 px-2 rounded bg-background-50/60 backdrop-blur-sm text-[11px] text-foreground-800">
              <i className="ri-star-fill text-accent-500 text-[10px]" />
              {current.rating.toFixed(1)}
            </span>
          </div>

          <h1 className="font-heading text-3xl md:text-5xl leading-tight text-foreground-950 tracking-wide">
            {current.title}
          </h1>
          <p className="mt-1 text-xs tracking-[0.2em] text-foreground-500 uppercase">{current.originalTitle}</p>

          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-foreground-600">
            <span>{current.region}</span>
            <span className="w-px h-3 bg-background-400/60" />
            <span>{current.year}</span>
            <span className="w-px h-3 bg-background-400/60" />
            <span>{current.status}</span>
            <span className="w-px h-3 bg-background-400/60" />
            <span>{current.episodes} 集</span>
            <span className="w-px h-3 bg-background-400/60" />
            <span>{formatDate(current.updatedAt)} 更新</span>
          </div>

          <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-lg line-clamp-3">
            {current.synopsis}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="h-11 px-6 rounded-md bg-primary-600 hover:bg-primary-500 text-foreground-950 text-sm flex items-center gap-2 transition-colors whitespace-nowrap"
            >
              查看作品详情
              <i className="ri-arrow-right-line" />
            </Link>
            <button
              type="button"
              onClick={() => toggleFavorite(current.id)}
              className={`h-11 px-5 rounded-md border text-sm flex items-center gap-2 transition-colors cursor-pointer whitespace-nowrap ${
                saved
                  ? "border-accent-500/70 bg-accent-500/10 text-accent-400"
                  : "border-background-400/60 text-foreground-700 hover:border-accent-500/70 hover:text-accent-400"
              }`}
            >
              <i className={saved ? "ri-heart-3-fill" : "ri-heart-3-line"} />
              {saved ? "已收藏" : "加入收藏"}
            </button>
          </div>
        </div>
      </div>

      <div className="absolute z-20 right-4 md:right-8 lg:right-12 bottom-6 md:bottom-8 flex items-center gap-3">
        <button
          type="button"
          onClick={prev}
          aria-label="上一部"
          className="w-9 h-9 md:w-10 md:h-10 rounded-full border border-background-400/60 bg-background-50/50 backdrop-blur-sm text-foreground-700 hover:text-accent-400 hover:border-accent-500/70 flex items-center justify-center transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-lg" />
        </button>
        <div className="flex items-center gap-1.5">
          {items.map((item, dotIndex) => (
            <button
              key={item.id}
              type="button"
              aria-label={`切换到 ${item.title}`}
              onClick={() => setIndex(dotIndex)}
              className={`h-1 rounded-full transition-all cursor-pointer ${
                dotIndex === index ? "w-6 bg-accent-500" : "w-3 bg-foreground-600/40 hover:bg-foreground-500/60"
              }`}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={next}
          aria-label="下一部"
          className="w-9 h-9 md:w-10 md:h-10 rounded-full border border-background-400/60 bg-background-50/50 backdrop-blur-sm text-foreground-700 hover:text-accent-400 hover:border-accent-500/70 flex items-center justify-center transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-lg" />
        </button>
      </div>
    </section>
  );
}